// Public application status page — reachable via /status?ref=app_xxx.
// The application_id is the secret. We surface only stage_name + status_label +
// submitted_at; no email, no decision note, no PII.
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CinematicCanvas, MonogramWatermark, Reveal, CornerBrackets } from "../components/brand/Cinematic";
import { LogoLockup } from "../components/brand/LogoLockup";
import { BrandFooter } from "../components/brand/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reusable lookup form — defined outside `Status` to satisfy
// react/no-unstable-nested-components. All state lives in the parent.
function StatusLookupForm({ headline, body, value, onChange, error, onSubmit }) {
  return (
    <form
      data-testid="status-lookup-form"
      onSubmit={onSubmit}
      className="mt-8 rounded-2xl border border-brand-text/15 bg-[#0F0F0F] p-5 sm:p-6"
    >
      <p data-testid="status-lookup-headline" className="font-display text-lg text-brand-text sm:text-xl">{headline}</p>
      {body && <p className="mt-2 text-sm text-brand-text/55">{body}</p>}
      <label className="mt-5 block">
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-text/45">
          Magic link or reference id
        </span>
        <input
          data-testid="status-lookup-input"
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="app_xxxxxxxx  or  https://omertabella.com/status?ref=app_…"
          autoComplete="off"
          spellCheck="false"
          className="mt-2 w-full rounded-md border border-brand-text/15 bg-brand-bg/70 px-3 py-2.5 text-sm text-brand-text placeholder:text-brand-text/30 focus:border-brand-gold focus:outline-none"
        />
      </label>
      {error && (
        <p data-testid="status-lookup-error" className="mt-2 text-xs text-rose-300/85">{error}</p>
      )}
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          data-testid="status-lookup-submit"
          className="inline-block rounded-full border border-brand-gold bg-brand-gold/[0.04] px-5 py-2 text-[11px] uppercase tracking-[0.28em] text-brand-gold transition-colors hover:bg-brand-gold hover:text-brand-bg"
        >
          Check status
        </button>
        <p className="text-[11px] text-brand-text/45">
          Don&apos;t have your link? <Link to="/apply" className="text-brand-gold hover:underline">Submit a new application</Link>.
        </p>
      </div>
    </form>
  );
}

const STATUS_STYLE = {
  received:          { tone: "text-brand-gold border-brand-gold/40",          blurb: "Leadership is reviewing your file. Replies arrive within 48 hours." },
  approved:          { tone: "text-emerald-300 border-emerald-300/40",        blurb: "You're in. Check your inbox for next steps and your portal link." },
  rejected:          { tone: "text-rose-300 border-rose-300/40",              blurb: "Not selected this round. You're welcome to re-apply when things shift." },
  changes_requested: { tone: "text-sky-300 border-sky-300/40",                blurb: "Leadership has questions. Watch your inbox for a follow-up." },
  archived:          { tone: "text-brand-text/60 border-brand-text/25",       blurb: "This application has been withdrawn." },
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "long", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function Status() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const ref = (params.get("ref") || "").trim();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [lookup, setLookup] = useState("");
  const [lookupErr, setLookupErr] = useState("");

  useEffect(() => { document.title = "Application Status · Omertàbella"; }, []);
  useEffect(() => {
    if (!ref) { setState({ loading: false, data: null, error: "missing_ref" }); return; }
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    axios.get(`${API}/public/applications/status`, { params: { ref } })
      .then((r) => { if (!cancelled) setState({ loading: false, data: r.data, error: null }); })
      .catch((e) => {
        if (cancelled) return;
        const code = e?.response?.status;
        setState({ loading: false, data: null, error: code === 404 ? "not_found" : "fetch_failed" });
      });
    return () => { cancelled = true; };
  }, [ref]);

  const style = state.data ? STATUS_STYLE[state.data.status] || STATUS_STYLE.received : null;

  // Pull a usable ref out of free-form input. Accepts:
  //   - a raw application_id (e.g. "app_71bbb4945b9a")
  //   - a full magic link (e.g. "https://omertabella.com/status?ref=app_…")
  //   - any URL with a ?ref= query
  const extractRef = (raw) => {
    const trimmed = (raw || "").trim();
    if (!trimmed) return "";
    if (/^app_[a-z0-9]+$/i.test(trimmed)) return trimmed;
    const match = trimmed.match(/[?&]ref=([^&\s#]+)/i);
    if (match && match[1]) return decodeURIComponent(match[1]).trim();
    // Last resort: if user pasted just a token-looking string, accept it.
    if (/^[a-z0-9_\-]{6,80}$/i.test(trimmed)) return trimmed;
    return "";
  };

  const submitLookup = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const extracted = extractRef(lookup);
    if (!extracted) {
      setLookupErr("Couldn't read a reference id. Paste the magic link from your email, or the ref starting with “app_”.");
      return;
    }
    setLookupErr("");
    navigate(`/status?ref=${encodeURIComponent(extracted)}`);
  };

  // Reusable lookup form — rendered on every non-success state so users can retry.
  // (Component definition lives outside `Status` to avoid React's no-unstable-nested-components
  // rule; we pass everything as props.)
  const lookupForm = (headline, body) => (
    <StatusLookupForm
      headline={headline}
      body={body}
      value={lookup}
      onChange={(v) => { setLookup(v); if (lookupErr) setLookupErr(""); }}
      error={lookupErr}
      onSubmit={submitLookup}
    />
  );

  return (
    <main data-testid="status-page" className="relative min-h-screen bg-brand-bg text-brand-text-warm">
      <CinematicCanvas />
      <MonogramWatermark className="right-[-25%] top-[5%]" />

      <header className="relative z-20 mx-auto flex max-w-3xl items-center justify-between px-6 pt-8 sm:px-10">
        <Link to="/" data-testid="status-back-link"
              className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-text/50 transition-colors hover:text-brand-gold">
          ← Home
        </Link>
        <LogoLockup variant="header" as={Link} to="/" />
        <span className="w-12" />
      </header>

      <div className="relative z-10 mx-auto max-w-2xl px-6 pb-32 pt-16 sm:px-10">
        <Reveal>
          <div className="glass relative overflow-hidden rounded-3xl p-8 sm:p-12">
            <CornerBrackets size={20} />
            <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-gold">Application status</p>

            {state.loading && (
              <div data-testid="status-loading" className="mt-8 text-center text-sm text-brand-text/60">
                Looking it up…
              </div>
            )}

            {!state.loading && state.error === "missing_ref" && (
              <div data-testid="status-missing-ref" className="mt-8">
                <h1 className="font-display text-3xl text-brand-text sm:text-4xl">Check your application.</h1>
                <p className="mt-3 text-sm text-brand-text/60">
                  Paste the magic link from your application emails — or the reference id starting with <span className="font-mono text-brand-gold">app_</span>.
                </p>
                {lookupForm(
                  "Look up by reference",
                  "If you've lost the link, just submit a new application — we reply within 48 hours either way."
                )}
              </div>
            )}

            {!state.loading && state.error === "not_found" && (
              <div data-testid="status-not-found" className="mt-8">
                <h1 className="font-display text-3xl text-brand-text sm:text-4xl">No application matches that reference.</h1>
                <p className="mt-3 text-sm text-brand-text/60">
                  Double-check the link from your email. If you applied recently, our team is responding within 48 hours either way.
                </p>
                <p className="mt-4 font-mono text-[11px] tracking-[0.18em] text-brand-text/40">REF · {ref || "—"}</p>
                {lookupForm("Try a different reference")}
              </div>
            )}

            {!state.loading && state.error === "fetch_failed" && (
              <div data-testid="status-fetch-failed" className="mt-8">
                <h1 className="font-display text-3xl text-brand-text sm:text-4xl">Couldn&apos;t load right now.</h1>
                <p className="mt-3 text-sm text-brand-text/60">
                  Please try again in a moment, or email us at{" "}
                  <a className="text-brand-gold hover:underline" href="mailto:team@omertabella.com">team@omertabella.com</a>.
                </p>
                {lookupForm("Retry your lookup")}
              </div>
            )}

            {!state.loading && state.data && (
              <div data-testid="status-card">
                <h1 className="mt-4 font-display text-3xl text-brand-text sm:text-4xl">
                  Hi, {state.data.stage_name}.
                </h1>
                <div
                  data-testid={`status-pill-${state.data.status}`}
                  className={`mt-6 inline-flex items-center gap-3 rounded-full border px-4 py-2 text-[11px] uppercase tracking-[0.28em] ${style.tone}`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-current" />
                  <span data-testid="status-label">{state.data.status_label}</span>
                </div>
                <p className="mt-5 text-sm text-brand-text/70">{style.blurb}</p>

                <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-brand-text/10 bg-brand-bg/40 p-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-text/40">Submitted</p>
                    <p data-testid="status-submitted-at" className="mt-2 text-sm text-brand-text">{fmt(state.data.submitted_at)}</p>
                  </div>
                  <div className="rounded-2xl border border-brand-text/10 bg-brand-bg/40 p-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-text/40">Reference</p>
                    <p data-testid="status-reference" className="mt-2 break-all font-mono text-[12px] text-brand-gold">{state.data.application_id}</p>
                  </div>
                </div>

                {state.data.decided_at && (
                  <p className="mt-6 text-xs text-brand-text/50">
                    Decision recorded {fmt(state.data.decided_at)}.
                  </p>
                )}

                <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.32em] text-brand-text/45">
                  We reply within 48 hours to every application.
                </p>
              </div>
            )}
          </div>
        </Reveal>

        <p className="mt-10 text-center text-xs text-brand-text/40">
          Questions? <a className="text-brand-gold hover:underline" href="mailto:team@omertabella.com">team@omertabella.com</a>
        </p>
      </div>

      <BrandFooter />
    </main>
  );
}
